import { useState, useRef, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  AppBar,
  Toolbar,
  ThemeProvider,
  createTheme,
  CssBaseline,
  Alert,
  Snackbar,
  Button,
  Chip,
  CircularProgress,
} from '@mui/material';
import { Help, Home, ExitToApp } from '@mui/icons-material';
import { AudioFile, ProcessingConfig } from './types';
import { useProcessingJob } from './hooks/useProcessingJob';
import FileUpload from './components/FileUpload';
import ProcessingConfigComponent from './components/ProcessingConfig';
import ProcessingInterface from './components/ProcessingInterface';
import UserGuide from './components/UserGuide';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoginScreen from './components/LoginScreen';
import CreditBalance from './components/CreditBalance';
import PricingModal from './components/PricingModal';
import PaymentsService from './services/payments';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

function AppContent() {
  const { user, loading: authLoading, signOut } = useAuth();
  const [targetFile, setTargetFile] = useState<AudioFile | null>(null);
  const [referenceFile, setReferenceFile] = useState<AudioFile | null>(null);
  const [referenceFile2, setReferenceFile2] = useState<AudioFile | null>(null);
  const [referenceBlend, setReferenceBlend] = useState<number>(50); // 0-100
  const [showGuide, setShowGuide] = useState(false);

  // Payment/Credits state
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [creditRefreshTrigger, setCreditRefreshTrigger] = useState(0);

  // Check for payment status in URL (after Stripe redirect)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payment = params.get('payment');

    if (payment === 'success') {
      // Invalidate cache and refresh credit balance
      PaymentsService.invalidateCache();
      setCreditRefreshTrigger(prev => prev + 1);
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);

      // Show success message
      setSnackbar({
        open: true,
        message: 'Payment successful! Your credits have been added.',
        severity: 'success',
      });
    } else if (payment === 'cancelled') {
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);

      setSnackbar({
        open: true,
        message: 'Payment cancelled.',
        severity: 'info',
      });
    }
  }, []);

  const [config, setConfig] = useState<ProcessingConfig>({
    outputFormat: 'pcm24',
    genre: 'auto',
    outputGainDb: 0,
    useLimiter: true,
    normalize: true,
    includeAnalysis: false,
    useFrequencyWeighting: false, // Post-processing EQ disabled by default
    useDithering: true, // Dithering enabled by default (recommended)
  });
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({ open: false, message: '', severity: 'info' });

  // Ref to scroll to processing section when job starts
  const processingSectionRef = useRef<HTMLDivElement>(null);

  const {
    currentJob,
    isProcessing,
    startProcessing,
    cancelProcessing,
    downloadFile,
    clearCurrentJob,
    runAnalysis,
  } = useProcessingJob();

  const canStart = targetFile && referenceFile && !isProcessing;

  const handleStartProcessing = async () => {
    if (!targetFile || !referenceFile) return;

    try {
      await startProcessing(targetFile, referenceFile, config, referenceFile2, referenceBlend);
      // Scroll to processing section after starting
      // Small delay to allow the section to render
      setTimeout(() => {
        processingSectionRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 100);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : 'Failed to start processing',
        severity: 'error',
      });
    }
  };

  const handleDownload = async (fileId: string, filename: string) => {
    try {
      await downloadFile({
        id: fileId,
        filename,
        format: '',
        size: 0,
        downloadUrl: '',
        createdAt: new Date(),
      });
      setSnackbar({
        open: true,
        message: `Downloaded ${filename}`,
        severity: 'success',
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to download file',
        severity: 'error',
      });
    }
  };

  const handleRunAnalysis = async () => {
    try {
      await runAnalysis();
      setSnackbar({
        open: true,
        message: 'Analysis started... Results will appear shortly',
        severity: 'info',
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : 'Failed to start analysis',
        severity: 'error',
      });
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AppBar position="static" elevation={0}>
        <Toolbar>
          <Box
            component="img"
            src="/apple-touch-icon.png"
            alt="US Mastering Online"
            onClick={() => setShowGuide(false)}
            sx={{
              width: 40,
              height: 40,
              mr: 2,
              cursor: 'pointer',
              '&:hover': { opacity: 0.8 }
            }}
          />
          <Typography
            variant="h6"
            component="div"
            onClick={() => setShowGuide(false)}
            sx={{
              flexGrow: 1,
              cursor: 'pointer',
              '&:hover': { opacity: 0.8 }
            }}
          >
            US Mastering Online
          </Typography>
          {user && (
            <>
              <CreditBalance
                onPurchaseClick={() => setShowPricingModal(true)}
                refreshTrigger={creditRefreshTrigger}
              />
              <Typography variant="body2" sx={{ mx: 2, opacity: 0.8 }}>
                {user.email}
              </Typography>
            </>
          )}
          <Button
            color="inherit"
            startIcon={showGuide ? <Home /> : <Help />}
            onClick={() => setShowGuide(!showGuide)}
            sx={{ textTransform: 'none', mr: 1 }}
          >
            {showGuide ? 'Back to App' : 'User Guide'}
          </Button>
          {user && (
            <Button
              color="inherit"
              startIcon={<ExitToApp />}
              onClick={signOut}
              sx={{ textTransform: 'none' }}
            >
              Sign Out
            </Button>
          )}
        </Toolbar>
      </AppBar>

      {authLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
          <CircularProgress />
        </Box>
      ) : !user ? (
        <LoginScreen />
      ) : (
        <Container maxWidth="lg" sx={{ py: 4 }}>
          {showGuide ? (
            <UserGuide />
          ) : (
            <>
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Box
            component="img"
            src="/us-mastering-logo-2026.png"
            alt="US Mastering Online"
            sx={{
              width: '100%',
              maxWidth: 900,
              height: 'auto',
              mx: 'auto',
              mb: 0,
              transform: 'scaleY(0.7)',
              filter: 'drop-shadow(0px 3px 6px rgba(0, 0, 0, 0.2))'
            }}
          />
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontStyle: 'italic', maxWidth: 700, mx: 'auto', mt: -1 }}
          >
            Professional audio mastering powered by 30+ years of industry experience from Grammy, MTV, Dove, and Billboard-nominated engineer Ludwing Diaz—match your track to any professional reference using advanced EQ matching and critical mastering techniques.
          </Typography>
        </Box>

        {/* How It Works Section - Moved to top */}
        <Paper elevation={1} sx={{ p: 3, mb: 4, bgcolor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
          <Typography variant="h6" gutterBottom>
            How it works
          </Typography>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' },
            gap: 3
          }}>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                1. Upload Files
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Upload your unmastered track and a professional reference track. Optionally add a second reference to blend.
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                2. Pre-Processing (Optional)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Apply de-esser to reduce harshness before mastering. Useful for mixes with
                excessive high-frequency energy.
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                3. Reference Matching
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Choose a genre preset or customize settings. The algorithm will match your target
                to the reference characteristics.
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                4. Post-Process & Download
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Fine-tune with transient preservation and frequency weighting, then analyze and
                download your mastered track.
              </Typography>
            </Box>
          </Box>
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setShowGuide(true)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              Watch Quick Tutorial
            </Button>
          </Box>
        </Paper>

        {/* File Upload Section - Side by Side */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 2,
          mb: 2
        }}>
          <Paper elevation={2} sx={{ p: 2 }}>
            <FileUpload
              title="Unmastered Track"
              subtitle="The track you want to master"
              file={targetFile}
              onFileSelect={setTargetFile}
              onFileRemove={() => setTargetFile(null)}
              disabled={isProcessing}
            />
          </Paper>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Paper elevation={2} sx={{ p: 2 }}>
              <FileUpload
                title="Reference Track 1"
                subtitle="Primary reference"
                file={referenceFile}
                onFileSelect={setReferenceFile}
                onFileRemove={() => setReferenceFile(null)}
                disabled={isProcessing}
              />
            </Paper>

            <Paper elevation={2} sx={{ p: 2 }}>
              <FileUpload
                title="Reference Track 2 (Optional)"
                subtitle="Blend with reference 1"
                file={referenceFile2}
                onFileSelect={setReferenceFile2}
                onFileRemove={() => setReferenceFile2(null)}
                disabled={isProcessing}
              />
            </Paper>

            {/* Blend Slider - only show when both references are uploaded */}
            {referenceFile && referenceFile2 && (
              <Paper elevation={2} sx={{ p: 1.5, bgcolor: 'info.light' }}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                  Reference Blend
                </Typography>
                <Box sx={{ px: 1, py: 0.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0.5 }}>
                    <Chip
                      label={
                        referenceBlend === 0 ? 'All Reference 1' :
                        referenceBlend === 100 ? 'All Reference 2' :
                        `${100 - referenceBlend}/${referenceBlend} Blend`
                      }
                      color="primary"
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                      Ref 1: {100 - referenceBlend}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                      Ref 2: {referenceBlend}%
                    </Typography>
                  </Box>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="1"
                    value={referenceBlend}
                    onChange={(e) => setReferenceBlend(parseInt(e.target.value))}
                    disabled={isProcessing}
                    style={{ width: '100%' }}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, fontSize: '0.65rem' }}>
                  Blends spectral characteristics
                </Typography>
              </Paper>
            )}
          </Box>
        </Box>

        {/* Network Performance Tip */}
        <Box sx={{
          mb: 2,
          p: 1.5,
          bgcolor: '#e3f2fd',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'info.light'
        }}>
          <Typography variant="body2" color="info.dark" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span>💡</span>
            <strong>Tip:</strong> For best experience, use a WiFi connection. Uploads may take 1-2 minutes on slower connections.
          </Typography>
        </Box>

        {/* Configuration Section */}
        <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
          <ProcessingConfigComponent
            config={config}
            onChange={setConfig}
            disabled={isProcessing}
            onStart={handleStartProcessing}
            canStart={Boolean(canStart)}
          />
        </Paper>

        {/* Processing Section - Shows status when active */}
        {currentJob && (
          <Paper elevation={2} sx={{ p: 2.5, mb: 4 }} ref={processingSectionRef}>
            <ProcessingInterface
              job={currentJob}
              onCancel={cancelProcessing}
              onDownload={handleDownload}
              onClear={clearCurrentJob}
              onRunAnalysis={handleRunAnalysis}
            />
          </Paper>
        )}
          </>
        )}
        </Container>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Pricing Modal */}
      <PricingModal
        open={showPricingModal}
        onClose={() => setShowPricingModal(false)}
      />
    </ThemeProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
